// Đọc NGHĨA của một dòng nhật ký lần chạy (StepLog) — dùng chung cho bảng plugin, Boss (#/runs) và bot.work.
// Nhật ký có 2 loại dòng: dòng CHÍNH của bước (khoá = khoá bước, kind = loại bước: do/extract/filter/…) mang kết quả bước; và dòng PHỤ gắn
// vào bước (khoá "<bước>~gac_cong|~xac_minh|~chan_doan|~nhip|~duyet|~sua|…", kind chan_doan/gac_cong/note/…) ghi việc máy làm quanh bước.
// status của dòng phụ nói về VIỆC PHỤ ĐÓ (vd. chan_doan "ok" = máy dò/AI chẩn đoán chạy xong — kể cả khi nó THẤY captcha), không phải
// "bước đạt". Trước đây mọi dòng status ok bị coi là "đạt"/"việc đã làm" → L-48 (dòng máy dò captcha hiện "đạt", đếm vào "2 đạt") và
// L-49 (dòng "~gac_cong" ok của lần chạy bị AI gác cổng BỎ QUA thành việc "bình luận" ảo; dòng "~duyet" ok thành việc trùng).

import { laDongCoTheDaDang, laDongDaXacMinh, laDongKhongThaySauDang, laDongXacMinhFb } from "./xac-minh-dang";

export interface DongNhatKyLite { key?: string; stepId?: string; kind: string; status: string; reason?: string }

/** Loại dòng PHỤ (không phải kết quả của bước). */
const KIND_PHU = new Set(["chan_doan", "gac_cong", "note", "kiem_noi_dung", "model", "luat", "heal", "interrupt", "reload"]);

export const laDongChanDoan = (l: DongNhatKyLite): boolean => l.kind === "chan_doan";

/** Dòng CHÍNH của một bước (kết quả bước), không phải dòng phụ. */
export function laDongBuoc(l: DongNhatKyLite): boolean {
  return !KIND_PHU.has(l.kind) && !(l.key ?? "").includes("~");
}

/** Dòng cho biết bước ĐÃ THỰC SỰ làm xong (dòng chính, status ok) — cơ sở ghi "Việc đã làm" (L-49). */
export function laDongViecDaLam(l: DongNhatKyLite): boolean {
  return l.status === "ok" && laDongBuoc(l);
}

/** Đếm "N đạt · M lỗi" của thẻ lần chạy: BỎ dòng chẩn đoán / máy dò (L-48). Dòng phụ khác giữ như cũ (không đổi số của các thẻ khác). */
export function demDatLoi(logs: DongNhatKyLite[]): { dat: number; loi: number } {
  let dat = 0, loi = 0;
  for (const l of logs) {
    if (laDongChanDoan(l) || (l.key ?? "").endsWith("~sau_dang")) continue; // ghi chú "chờ rồi tải lại để xác minh" không phải bước đạt
    if (l.status === "ok") dat++;
    else if (l.status === "fail" || l.status === "halt") loi++;
  }
  return { dat, loi };
}

const RE_CAN_NGUOI = /^(Máy dò trang|AI chẩn đoán|AI nhìn ảnh chụp): (xac_minh|can_dang_nhap)\b/;

/**
 * Nhãn cho một dòng chẩn đoán (kind chan_doan) — null nếu không phải dòng chẩn đoán (giữ nhãn đạt/lỗi/bỏ qua như cũ).
 * Máy dò / AI thấy captcha, xác minh, trang đăng nhập → "cần người" (đỏ); AI không chẩn đoán được → "chẩn đoán" (vàng); còn lại trung tính.
 */
export function nhanDongChanDoan(l: DongNhatKyLite): { chu: string; cls: "err" | "warn" | ""; goiY: string } | null {
  if (!laDongChanDoan(l)) return null;
  if ((l.key ?? "").endsWith("~xac_minh") || RE_CAN_NGUOI.test(l.reason ?? "")) {
    return { chu: "cần người", cls: "err", goiY: "Máy thấy trang bắt CAPTCHA / xác minh / đăng nhập — cần người xử lý trên trình duyệt của bot; máy đã dừng, không tính là bước đạt" };
  }
  if (l.status === "fail") return { chu: "chẩn đoán", cls: "warn", goiY: "AI không chẩn đoán được lỗi của bước này — xem ghi chú" };
  return { chu: "chẩn đoán", cls: "", goiY: "Ghi chú chẩn đoán của máy/AI cho bước ở trên — không phải kết quả bước" };
}

/**
 * XÁC MINH SAU KHI ĐĂNG (src/shared/xac-minh-dang.ts) — nhãn cho dòng chính của bước gửi: "không thấy sau tải lại" (vàng; dòng skip — không tính
 * đạt, không phải việc đã làm) / "đạt · đã xác minh" (xanh). null = dòng khác (giữ nhãn đạt/lỗi/bỏ qua như cũ).
 */
export function nhanDongSauDang(l: DongNhatKyLite): { chu: string; cls: "ok" | "warn" | ""; goiY: string } | null {
  if ((l.key ?? "").endsWith("~sau_dang")) return { chu: "xác minh", cls: "", goiY: "Máy chờ rồi tải lại trang để kiểm bình luận vừa gửi còn hiện không (chỉ đọc) — không phải kết quả bước; kết quả ở dòng chính của bước" };
  if (laDongKhongThaySauDang(l)) return { chu: "không thấy sau tải lại", cls: "warn", goiY: "Đã gửi, nhưng tải lại trang thì KHÔNG thấy bình luận — mạng xã hội có thể đã ẩn/giữ lại; không tính là đăng thành công, máy không gửi lại (tránh trùng)" };
  if (laDongCoTheDaDang(l)) return { chu: "có thể đã đăng", cls: "warn", goiY: "Bình luận CÓ THỂ ĐÃ đăng nhưng máy không xác minh được — đã ghi sổ để vòng sau KHÔNG đăng lại; hãy mở bài kiểm tra bằng mắt" };
  if (laDongXacMinhFb(l)) return { chu: "đạt · đã xác minh", cls: "ok", goiY: "Bước báo lỗi/mơ hồ nhưng máy đọc lại bài thấy bình luận của nick — tính đã xong, đã ghi sổ, không gửi lại" };
  if (laDongDaXacMinh(l)) return { chu: "đạt · đã xác minh", cls: "ok", goiY: "Bước làm xong; tải lại trang vẫn thấy bình luận của nick" };
  return null;
}
